import { useState, type FormEvent } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ChevronRight, Plus, ShoppingBasket } from "lucide-react";
import { de } from "@ainkauf/core";
import { Wordmark } from "@/components/Wordmark";
import { SyncBadge } from "@/components/SyncBadge";
import { BottomNav } from "@/components/BottomNav";
import { MicButton } from "@/components/MicButton";
import { allLists, createList, itemsOfList } from "@/db/repository";
import { listenFarbe } from "@/lib/list-color";
import { useNavigate } from "@/lib/router";

/** Übersicht aller Listen mit der Zahl der offenen Artikel. */
export function ListsScreen() {
  const go = useNavigate();
  const [name, setName] = useState("");

  const lists = useLiveQuery(async () => {
    const rows = await allLists();
    return Promise.all(
      rows.map(async (list) => {
        const items = await itemsOfList(list.id);
        return { list, open: items.filter((item) => !item.is_checked).length, gesamt: items.length };
      }),
    );
  }, [], []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setName("");
    const created = await createList(trimmed);
    go(`/liste/${created.id}`);
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-4 px-4 mit-leiste">
      <header className="flex items-center justify-between gap-3 pb-1 pt-5">
        <h1 className="text-[2rem]">
          <Wordmark />
        </h1>
        <span className="flex items-center gap-2">
          <SyncBadge />
          <MicButton currentListId={null} />
        </span>
      </header>

      <main className="flex flex-1 flex-col gap-4">
        <form onSubmit={(event) => void submit(event)} className="flex gap-2">
          <input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder={de.lists.namePlaceholder}
            aria-label={de.lists.nameLabel}
            data-testid="list-name-input"
            className="karte min-h-[52px] flex-1 px-4 text-ink outline-none placeholder:text-ink-3"
          />
          <button
            type="submit"
            aria-label={de.lists.create}
            data-testid="list-create"
            className="knopf-haupt inline-flex size-[52px] shrink-0 items-center justify-center"
          >
            <Plus size={22} strokeWidth={2.5} aria-hidden />
          </button>
        </form>

        {lists.length === 0 ? (
          <p className="karte px-5 py-10 text-center text-ink-2">{de.lists.empty}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {lists.map(({ list, open, gesamt }, i) => {
              const farbe = listenFarbe(list.name);
              const anteil = gesamt === 0 ? 0 : (gesamt - open) / gesamt;
              return (
                <li key={list.id} className="auftritt" style={{ ["--takt" as string]: `${String(Math.min(i * 40, 320))}ms` }}>
                  <button
                    type="button"
                    onClick={() => {
                      go(`/liste/${list.id}`);
                    }}
                    data-testid="list-row"
                    data-name={list.name}
                    className="karte kante flex min-h-[82px] w-full items-center gap-3.5 py-3 pl-4 pr-4 text-left transition-transform active:scale-[0.99]"
                    style={{ ["--kante-farbe" as string]: farbe }}
                  >
                    {/* Gefärbte Kachel statt Aufzählungspunkt: gibt jeder Liste
                        ein Gesicht, das man von oben wiedererkennt. */}
                    <span
                      aria-hidden
                      className="inline-grid size-11 shrink-0 place-items-center rounded-[var(--radius-md)]"
                      style={{ background: `color-mix(in oklab, ${farbe} 18%, #fff)`, color: farbe }}
                    >
                      <ShoppingBasket size={21} strokeWidth={2} />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-display text-xl font-extrabold tracking-tight text-ink">{list.name}</span>
                      <span className="num block text-sm text-ink-2">
                        {open === 0 ? (gesamt === 0 ? de.lists.empty : de.lists.allDone) : de.items.openCount(open)}
                      </span>
                      {gesamt > 0 && (
                        <span aria-hidden className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-paper-2">
                          <span
                            className="block h-full rounded-full"
                            style={{ width: `${String(Math.round(anteil * 100))}%`, background: farbe, transition: "width 420ms var(--ease-out)" }}
                          />
                        </span>
                      )}
                    </span>

                    <ChevronRight size={20} strokeWidth={1.5} className="shrink-0 text-ink-3" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <BottomNav active="listen" />
    </div>
  );
}
